import { Injectable } from "@angular/core";
import { CheckoutSession } from "src/services/payment/payment.interface";
import { isInKioskMode } from "src/services/utils/kiosk-mode.utils";
import { environment } from "@environments/environment";
import { kioskPages, redesignPages } from "src/services/navigation/paths.const";
import { ProductBought } from "../../app/redesign/customer/thank-you/thank-you.page";
import { AuthService } from "src/services/auth/auth.service";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser/ngx";
import { MessageService } from "src/services/message/message.service";
import { PaymentsService } from "src/services/payment/payments.service";
import { SubscriptionTypeService } from "../subscription-type/subscription-type.service";
import { PlanFrequency, PlanUniqueId } from "../subscription-type/subscription-type.interface";
import { ApiService } from "../http/api.service";
import { BackendBody, SetupIntent } from "../../shared/backend-api.types";
import { apiPath } from "../../shared/api-path.const";

@Injectable({
  providedIn: "root",
})
export class CheckoutService {
  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly inAppBrowser: InAppBrowser,
    private readonly messageService: MessageService,
    private readonly paymentsService: PaymentsService,
    private readonly subscriptionTypeService: SubscriptionTypeService,
  ) {}

  public async createSetupIntent(userId: string): Promise<SetupIntent> {
    return this.apiService.sendPostRequest<BackendBody["CreateSetupIntentRequestBody"], SetupIntent>(
      apiPath.setupIntent,
      { userId },
    );
  }

  public async proceedWithPayment(
    planId: PlanUniqueId,
    frequency: PlanFrequency,
    coupon?: string,
    orderId?: string,
  ): Promise<void> {
    let session: CheckoutSession;

    const planToPayFor = await this.subscriptionTypeService.getPlanFromIdAndFrequency(planId, frequency);
    const activeUser = await this.authService.getActiveUser();

    if (!activeUser?.id) {
      await this.messageService.presentErrorToast("You don't appear to be logged in. Go back and log in again.");
      return;
    }

    if (!planToPayFor) {
      await this.messageService.presentErrorToast("We couldn't find the selected plan. Please try again.");
      return;
    }

    try {
      let successRedirectUrl: string;
      if (isInKioskMode()) {
        successRedirectUrl = `${environment.endpoints.frontendDomain}/${kioskPages.thankYou}`;
      } else {
        const productBought: ProductBought = "subscription";
        successRedirectUrl = `${environment.endpoints.frontendDomain}/${redesignPages.thankYou}/${productBought}`;
      }

      session = await this.paymentsService.createStripePaymentSessionForSubscription(
        activeUser.id,
        successRedirectUrl,
        planToPayFor.stripeId,
        coupon ?? undefined,
        orderId,
      );
    } catch (error) {
      await this.messageService.presentErrorToast(JSON.stringify(error));
      return;
    }

    const browser = this.inAppBrowser.create(session.url, "_self", { beforeload: "get" });
    browser.show();
  }
}
